import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Car } from "lucide-react";
import { Seo } from "@/components/Seo";
import { useLanguage } from "@/context/LanguageContext";
import { mediaUrl } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";
import { VehicleGallery, VehicleSpecs } from "@/components/vehicles/VehicleDialog";
import { VehicleInterestForm } from "@/components/vehicles/VehicleInterestForm";
import { VehicleShare } from "@/components/vehicles/VehicleShare";
import { priceLabel } from "@/components/vehicles/vehicleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehiclePage = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const tv = t.vehicles;
  const [v, setV] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    setState("loading");
    axios.get(`${API}/vehicles/${id}`).then((r) => { setV(r.data); setState("ok"); }).catch(() => setState("missing"));
  }, [id]);

  return (
    <main className="min-h-screen bg-mint/40 pb-24 pt-32" data-testid="vehicle-page">
      <Seo page="vehicles" title={v ? `${v.title} · ${tv.kind[v.kind]} · ${priceLabel(v, tv)}` : undefined} description={v?.description || undefined} image={v?.photos?.[0] ? mediaUrl(v.photos[0]) : undefined} />
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Link to="/vehicules" data-testid="vehicle-page-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand hover:text-gold"><ArrowLeft className="h-3.5 w-3.5" />{tv.back}</Link>
        {state === "missing" && (
          <div className="mt-10 rounded-3xl bg-white p-10 text-center shadow-xl shadow-brand/5" data-testid="vehicle-page-missing">
            <Car className="mx-auto h-12 w-12 text-brand/40" />
            <p className="font-display mt-4 text-xl font-bold text-forest">{tv.notFound}</p>
            <Link to="/vehicules" className="mt-6 inline-flex rounded-full bg-gold px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white">{tv.all}</Link>
          </div>
        )}
        {v && (
          <Reveal>
            <article className="mt-8 grid gap-8 rounded-3xl border border-brand/10 bg-white p-5 shadow-xl shadow-brand/5 sm:p-8 lg:grid-cols-5">
              <div className="lg:col-span-3"><VehicleGallery v={v} testid="vehicle-page" /></div>
              <div className="lg:col-span-2">
                <span className={`inline-block rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white ${v.kind === "rent" ? "bg-brand" : "bg-gold"}`}>{tv.kind[v.kind]}</span>
                <h1 className="font-display mt-3 text-3xl font-extrabold text-forest sm:text-4xl" data-testid="vehicle-page-title">{v.title}</h1>
                <p className="font-display mt-2 text-2xl font-extrabold text-gold" data-testid="vehicle-page-price">{priceLabel(v, tv)}</p>
                <div className="mt-5"><VehicleSpecs v={v} /></div>
                {v.description && <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-brand-hover" data-testid="vehicle-page-description">{v.description}</p>}
                <div className="mt-6"><VehicleShare v={v} prefix="vehicle-page-share" /></div>
                <div className="mt-6"><VehicleInterestForm v={v} /></div>
              </div>
            </article>
          </Reveal>
        )}
      </div>
    </main>
  );
};

export default VehiclePage;
